<template>
    <div style="text-align: center;">
        <BriefcaseIcon style="width: 60px;" />
        <h1 class="p-text-center">
            {{ t("registration.tech.headline2") }}
        </h1>
        <Timeline :step="step" />
    </div>
    <div>
        <span class="p-float-label">
            <Dropdown v-model="fullTimeOptionSelection"
                      :options="fulltimeOptions"
                      optionLabel="name"
                      optionValue="value"
                      data-cy="wizard-fullTime" />
            <label>{{t("registration.tech.fulltime")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.fullTime.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-3">
        <span class="p-float-label">
            <InputNumber v-model="v$.desiredSalary.$model"
                         mode="currency"
                         currency="EUR"
                         locale="de-DE"
                         :class="{'p-invalid':v$.desiredSalary.$error}"
                         data-cy="wizard-salary" />
            <label>{{t("registration.tech.salary")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.desiredSalary.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-3">
        <span class="p-float-label">
            <InputNumber v-model="v$.desiredSalaryBonus.$model"
                         mode="currency"
                         currency="EUR"
                         locale="de-DE"
                         :class="{'p-invalid':v$.desiredSalaryBonus.$error}"
                         data-cy="wizard-bonus" />
            <label>{{t("registration.tech.bonus")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.desiredSalaryBonus.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-3">
        <span class="p-float-label">
            <Dropdown v-model="v$.noticePeriodId.$model"
                      :options="noticePeriodOptions"
                      optionLabel="name"
                      optionValue="id"
                      data-cy="wizard-noticeperiod" />
            <label>{{t("registration.tech.noticeperiod")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.noticePeriodId.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-3">
        <span class="p-float-label">
            <Dropdown :options="changeOptions"
                      v-model="changeOptionSelection"
                      optionLabel="name"
                      optionValue="value"
                      data-cy="job-preferences-dropdown" />
            <label>{{t("job.preferences.choose.willingnesstochange")}}</label>
        </span>
        <div class="p-mt-3">
            <span class="p-no-float-label">
                <ul class="tags"
                    v-if="user.changeMotivations?.length && user.changeMotivations.length>0">
                    <li v-for="x in user.changeMotivations" :key="x.id">
                        {{t("job.preferences.changemotivations." + x.id)}}
                        <Button icon="pi pi-times"
                                class="p-button-rounded p-button-outlined"
                                @click="handleMotivationRemoval(x)" />
                    </li>
                </ul>
                <MultiSelect v-model="v$.changeMotivations.$model"
                             display="none"
                             :class="{ 'p-invalid': v$.changeMotivations.$error }"
                             :options="motivationOptions"
                             optionLabel="name"
                             data-cy="job-preferences-change-motivation">
                    <template #option="slotProps">
                        <div class="list-item-text">
                            {{ slotProps.option.name }}
                        </div>
                    </template>
                </MultiSelect>
                <label>{{t("job.preferences.choose.changemotivation")}}</label>
                <small class="p-error p-mt-0"
                       v-for="(error, index) of v$.changeMotivations.$errors"
                       :key="index">
                    {{error.$message}}
                </small>
            </span>
        </div>
    </div>
    <div class="call-to-action mobile-bottom-nav">
        <Button data-cy="tech-reg-button-back"
                class="button-secondary button-back p-pl-3"
                @click="prev()">
            <i class="pi pi-angle-left" />{{t("back")}}
        </Button>
        <Button data-cy="tech-reg-button-next"
                class="button-primary button-next p-pr-3"
                @click="submit()">
            {{isDirectSignUp ? t("continue") : t("submit")}}
            <i class="pi pi-angle-right" />
        </Button>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, computed } from 'vue';
    import { useI18n } from "vue-i18n";
    import { BriefcaseIcon } from "@heroicons/vue/outline";
    import useVuelidate from '@vuelidate/core';
    import InputNumber from 'primevue/inputnumber';
    import Timeline from "@/registration/tech/wizard/Timeline.vue";
    import { WillingnessToChange } from "@/models/WillingnessToChange";
    import { ChangeMotivation } from "@/models/ChangeMotivation";
    import { createChangeMotivationService } from "@/services/changeMotivationService";

    const props = defineProps(["candidate", "step", "isDirectSignUp"]);
    const emit = defineEmits(['submit', 'previous']);
    const step = ref(props.step);
    const user = ref(props.candidate);
    const { t } = useI18n();
    type FulltimeOption = {
        name: string;
        value: number;
        innerValue: boolean | null;
    };
    const fulltimeOptions = ref<FulltimeOption[]>();
    fulltimeOptions.value = [
        { value: 1, innerValue: true, name: t("fulltime") },
        { value: 2, innerValue: false, name: t("parttime.greater") },
        { value: 3, innerValue: null, name: t("parttime.lesser") },
    ] as FulltimeOption[];
    type ChangeOption = {
        name: string;
        innerValue: WillingnessToChange;
    };
    const fullTimeOptionSelection = computed({
        get: () => {
            const selected = fulltimeOptions.value?.find(
                (option) => option.innerValue == user.value.fullTime
            );
            return selected?.value;
        },
        set: (value) => {
            if (value) {
                const selected = fulltimeOptions.value?.find(
                    (option) => option.value == value
                );
                user.value.fullTime = selected?.innerValue;
            }
        },
    });

    const handleMotivationRemoval = async (option: ChangeMotivation) => {
        const index = user.value.changeMotivations.indexOf(option);
        if (index !== -1) user.value.changeMotivations.splice(index, 1);
    };

    const changeOptionSelection = computed({
        get: () => {
            const selected = changeOptions.value?.find(
                (option) => option.value == user.value.willingnessToChange
            );
            return selected?.value;
        },
        set: (value) => {
            if (value) {
                const selected = changeOptions.value?.find(
                    (option) => option.value == value
                );
                user.value.willingnessToChange = selected?.value;
            }
        },
    });

    const noticePeriodOptions = [1, 8, 7, 6, 5, 247, 244, 246, 4, 2, 245, 3, 9, 10]
        .map(x => { return { id: x, name: t('registration.tech.noticeperiod.' + x) } });

    const changeOptions = ref<ChangeOption[]>();
    changeOptions.value = [
        {
            value: WillingnessToChange.VeryHigh,
            name: t("profilepage.willingnesstochange.4"),
        },
        {
            value: WillingnessToChange.RatherHigh,
            name: t("profilepage.willingnesstochange.3"),
        },
        {
            value: WillingnessToChange.RatherLow,
            name: t("profilepage.willingnesstochange.2"),
        },
    ];

    const motivationOptions = ref<ChangeMotivation[]>();
    const fetchChangeMotivations = async () => {
        const service = await createChangeMotivationService();
        const response = (await service.getByBrandId(1)).data;
        motivationOptions.value = response.map(x => ({ id: x.id, name: t("job.preferences.changemotivations." + x.id) }))
    }

    const rules = computed(() => {
        return {
            fullTime: {},
            desiredSalary: {},
            desiredSalaryBonus: {},
            noticePeriodId: {},
            changeMotivations: {},
        }
    });
    const v$ = useVuelidate(rules, user);
    const validate = () => {
        v$.value.$touch();
        return v$.value.$error;
    }

    const submit = async () => {
        if (validate())
            return;

        emit("submit");
    }
    const prev = async () => {
        emit("previous");
    }

    onMounted(async () => {
        await fetchChangeMotivations();
    });

</script>

<style scoped>
    .p-inputnumber.p-component.p-inputwrapper.p-inputwrapper-filled,
    .p-inputnumber.p-component.p-inputwrapper {
        width: 100%;
    }

    .p-float-label > span ~ label {
        top: 50% !important;
        left: 0.5rem !important;
    }

    .p-float-label .p-inputwrapper-focus ~ label,
    .p-float-label .p-inputwrapper-filled ~ label {
        top: 10px !important;
        left: 15px !important;
    }

    .p-float-label .p-inputwrapper-focus ~ label {
        top: 10px !important;
        left: 15px !important;
    }
</style>
<style>
    @media (max-width: 565px) {
        .p-multiselect-panel .p-multiselect-items .p-multiselect-item {
            white-space: unset !important;
        }

            .p-multiselect-panel .p-multiselect-items .p-multiselect-item .list-item-text {
                overflow-wrap: break-word;
            }
    }
</style>
